import { Theme } from "@material-ui/core/styles/createMuiTheme";
import makeStyles from "@material-ui/styles/makeStyles";
import React, { useEffect, useState } from "react";
import AphTextField from "../web-ui-components/AphTextField";
import AphButton from "../web-ui-components/AphButton";
import CircularProgress from "@material-ui/core/CircularProgress";
import PlacesAutocomplete, {
  geocodeByAddress,
  getLatLng,
} from "react-places-autocomplete";
import {
  GetPatientAddressList,
  GetPatientAddressListVariables,
} from "graphql/types/GetPatientAddressList";
import { useApolloClient } from "react-apollo-hooks";
import { GET_PATIENT_ADDRESSES_LIST } from "graphql/address";
import { useAuth } from "hooks/authHooks";
import {
  getDiagnosticServiceability,
  getDiagnosticServiceabilityVariables,
} from "graphql/types/getDiagnosticServiceability";
import { GET_DIAGNOSTIC_SERVICEABILITY } from "graphql/diagnostics";
import { useDiagnosticsCart } from "./DiagnosticsCartProvider";
import { useGlobalDiagnosticsCart } from "./DiagnosticsCartProviderGlobal";
import { DiagnosticAddressByPinCode } from "components/interfaces/DiagnosticAddressByPinCode";
import { clevertapTracking } from "clevertapTracking";
import { getCommonPatientAttributes } from "helpers/commonHelpers";
import { DiagnosticsBookingSource } from "graphql/types/globalTypes";
import { dataLayerTracking } from "gtmTracking";

type SuggestionProps = {
  active: boolean;
  description: string;
  id: string;
  index: number;
  placeId: string;
  formattedSuggestion: {
    mainText: string;
    secondaryText: string;
  };
  matchedSubstrings: Array<{ length: number; offset: number }>;
};
type InputProps = {
  getInputProps: Function;
  suggestions: Array<SuggestionProps>;
  getSuggestionItemProps: Function;
  loading: boolean;
};

interface TestLocationProps {
  locateCurrentLocation: () => void;
  updateToDefaultLocation: boolean;
  setUpdateToDefaultLocation: (value: boolean) => void;
  isUserDeniedLocationAccess: boolean;
  setIsUserDeniedLocationAccess: (accessDenied: boolean) => void;
  isLoading: boolean;
  setIsLoading: (isLoading: boolean) => void;
  isFetchingLocation: boolean;
}

const useStyles = makeStyles((theme: Theme) => {
  return {
    searchWrap: {
      background: "#fff",
      padding: 16,
      paddingTop: 0,
    },
    medicineSearchForm: {
      backgroundColor: "#f7f8f5",
      display: "flex",
      alignItems: "center",
      borderRadius: 5,
      position: "relative",
    },
    searchBtn: {
      marginLeft: "auto",
      padding: 0,
      boxShadow: "none",
      backgroundColor: "transparent !important",
      minWidth: "auto",
      position: "absolute",
      right: 10,
      cursor: "default",
    },

    searchInputBox: {
      "& input": {
        padding: "11px 12px",
        fontSize: 14,
        border: "1px solid rgba(0, 0, 0, 0.1)",
        borderRadius: 5,
        "&:focus": {
          border: "1px solid #00b38e",
        },
      },
      "& >div": {
        "&:after": {
          display: "none",
        },
        "&:before": {
          display: "none",
        },
      },
    },
    searchBtnDisabled: {
      opacity: 0.5,
      "& img": {
        filter: "grayscale(100%)c",
      },
    },
    findLocation: {
      paddingTop: 12,
      display: "flex",
      alignItems: "center",
      fontSize: 13,
      fontWeight: 600,
      color: "#FC9916",
      "& img": {
        paddingRight: 5,
      },
      cursor: "pointer",
    },
    locationWrap: {
      background: "#f7f8f5",
      borderRadius: "0 0 10px 10px",
    },
    address: {
      background: "#fff",
      padding: 16,
      marginTop: 10,
      height: "50vh",
      overflow: "scroll",
      borderRadius: "0 0 10px 10px",
    },
    addressList: {},
    addressTitle: {
      color: "rgba(2, 71, 91, 0.5)",
      fontSize: 12,
      fontWeight: 500,
      marginBottom: 10,
      textTransform: "uppercase",
    },
    addressListWrap: {
      display: "flex",
      flexWrap: "wrap",
      justifyContent: "space-between",
    },
    addressTile: {
      border: "1px solid #CECECE",
      boxSizing: "border-box",
      borderRadius: 10,
      padding: 10,
      flexBasis: "48.5%",
      marginBottom: 9,
      cursor: "pointer",
    },
    tileTitle: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
    },
    tileTitleContent: {
      fontSie: 14,
      fontWeight: 500,
      display: "flex",
      alignItems: "center",
      "& img": {
        verticalAlign: "middle",
        paddingRight: 5,
      },
      "& span": {
        background: "#FFF0B3",
        borderRadius: 10,
        fontWeight: 500,
        fontSize: 10,
        padding: 5,
      },
    },
    tileDetail: {
      paddingTop: 5,
      "& p": {
        margin: 0,
        fontSize: 12,
        overflow: "hidden",
        display: "-webkit-box",
        lineClamp: 2,
        boxOrient: "vertical",
      },
    },

    searchList: {
      "&>p": {
        fontSize: 12,
      },
      "& ul": {
        margin: 0,
        padding: 0,
        "& li": {
          padding: "10px 0px",
          listStyleType: "none",
          display: "flex",
          flexDirection: "row",
          position: "relative",
          "&:after": {
            content: '""',
            display: "block",
            background: "#ECECEC",
            position: "absolute",
            height: 1,
            width: "92%",
            right: 0,
            bottom: 0,
          },
        },
      },
    },
    searchListIcon: {
      "& img": {
        verticalAlign: "middle",
      },
      "& h4": {
        margin: 0,
        fontSize: 14,
        fontWeight: 500,
      },
      "& p": {
        margin: 0,
        fontSize: 12,
        overflow: "hidden",
        display: "-webkit-box",
        lineClamp: 1,
        boxOrient: "vertical",
        paddingTop: 2,
      },
      "&:first-child": {
        paddingRight: 7,
      },
    },
    locationAlert: {
      background: "#02475B",
      color: "#fff",
      fontSize: 11,
      fontWeight: 400,
      position: "relative",
      marginTop: 4,
      padding: 4,
      "&>img": {
        position: "absolute",
        top: -8,
        left: 17,
      },
    },
    locationErrMsg: {
      display: "flex",
      alignItems: "flex-start",
      "& img": {
        paddingRight: 5,
      },
    },
    loading: {
      position: "absolute",
      left: "43%",
      top: "53%",
    },
    fetchAlert: {
      background: "#F7F8F5",
      border: "1px solid rgba(0, 0, 0, 0.1)",
      borderRadius: 5,
      fontSize: 14,
      fontWeight: 600,
      padding: 12,
      display: "flex",
      flexDirection: "row",
      justifyContent: "space-between",
      "& button": {
        padding: 0,
        background: "unset",
        boxShadow: "unset",
        color: "#FC9916",
      },
    },
    searchResult: {
      "-webkit-box-orient": "vertical",
      "-webkit-line-clamp": "1",
      overflow: "hidden",
      display: "-webkit-box",
    },
  };
});

export const TestLocation: React.FC<TestLocationProps> = (props) => {
  const {
    locateCurrentLocation,
    isUserDeniedLocationAccess,
    setIsUserDeniedLocationAccess,
    setIsLoading,
    isLoading,
    isFetchingLocation,
    updateToDefaultLocation,
    setUpdateToDefaultLocation,
  } = props;
  const classes = useStyles({});
  const client = useApolloClient();
  const { currentPatient } = useAuth();
  const [searchInput, setSearchInput] = useState<string>("");
  const [isSearched, setIsSearched] = useState<boolean>(false);
  const {
    setDeliveryAddresses,
    setDeliveryAddressId,
    deliveryAddresses,
    setFallbackAreaName,
    setCartCityId,
    setOpenLocation,
    setShowLocationPopup,
    setShouldDefault,
    addCartInfo,
  } = useDiagnosticsCart();
  const {
    setDiagnosticAddress,
    setServiceability,
  } = useGlobalDiagnosticsCart();

  const handleChange = (address: string) => {
    setIsUserDeniedLocationAccess(false);
    setSearchInput(address);
    setIsSearched(address.length > 2);
  };

  const getServiceability = (
    latitude: number,
    longitude: number,
    source?: string,
    pincode?: string,
    city?: string,
    logEvent?: boolean
  ) => {
    setFallbackAreaName("");
    setIsLoading(true);
    let serviceable = false;
    let diagAddress: DiagnosticAddressByPinCode;
    client
      .query<getDiagnosticServiceability, getDiagnosticServiceabilityVariables>(
        {
          query: GET_DIAGNOSTIC_SERVICEABILITY,
          variables: {
            longitude,
            latitude,
            source: DiagnosticsBookingSource.WEB,
          },
        }
      )
      .then(({ data }) => {
        if (data?.getDiagnosticServiceability?.status) {
          const serviceabilityData =
            data?.getDiagnosticServiceability?.serviceability;
          diagAddress = {
            cityID: serviceabilityData?.cityID || 0,
            cityName: serviceabilityData?.city || "",
            stateID: serviceabilityData?.stateID || 0,
            stateName: serviceabilityData?.state || "",
            longitude: longitude,
            latitude: latitude,
            pinCode: pincode,
          };
          setCartCityId(diagAddress?.cityID);
          setDiagnosticAddress(diagAddress);
          setServiceability(true);
          if (source !== "Saved Address") {
            addCartInfo({
              cartAddressId: null,
            });
            setDeliveryAddressId(null);
          }
          serviceable = true;
        } else {
          city && setFallbackAreaName(city);
          setServiceability(false);
          if (source !== "Saved Address") {
            addCartInfo({
              cartAddressId: null,
            });
            setDeliveryAddressId(null);
          }
        }
      })
      .catch(() => {
        setServiceability(false);
        serviceable = false;
      })
      .finally(() => {
        setShowLocationPopup(false);
        setOpenLocation(false);
        setIsLoading(false);
        const pageSource = location.pathname.includes("/lab-tests/")
          ? "Test Detail Page"
          : location.pathname.includes("/lab-tests")
          ? "Home Page"
          : location.pathname.includes("/test-listing")
          ? "Test Listing Page"
          : "Search Results Page";
        const addressEventData = {
          ...getCommonPatientAttributes(currentPatient),
          latitude: latitude || "",
          longitude: longitude || "",
          City: diagAddress?.cityName || city || "",
          CityId: diagAddress?.cityID || "Not available",
          state: diagAddress?.stateName || "",
          pincode: Number(pincode) || "",
          Serviceability: serviceable ? "Yes" : "No",
          Source: pageSource || "",
          "Selection type": "Manual",
        };
        logEvent &&
          clevertapTracking("Diagnostic address entered", addressEventData);
      });
    setSearchInput("");
  };

  const handleSelect = (address: string) => {
    setIsLoading(true);
    let pincode = "";
    let cityName = "";
    geocodeByAddress(address)
      .then((results: any) => {
        const addressComponents = results?.[0]?.address_components;
        const resWithPincode = addressComponents?.find((addComponent: any) =>
          addComponent.types.includes("postal_code")
        );

        const cityObj =
          addressComponents?.find((addComponent: any) =>
            addComponent.types.includes("administrative_area_level_2")
          ) ||
          addressComponents?.find((addComponent: any) =>
            addComponent.types.includes("administrative_area_level_3")
          );
        const stateObj = addressComponents?.find((addComponent: any) =>
          addComponent.types.includes("administrative_area_level_1")
        );

        pincode = resWithPincode ? resWithPincode.long_name : "";
        cityName = cityObj?.long_name ?? stateObj?.long_name;
        dataLayerTracking({
          event: "location_selected",
          patient_location: `${cityObj?.long_name}_${stateObj?.long_name}`,
          state: stateObj.long_name,
          section: "Lab Tests",
        });
        return getLatLng(results[0]);
      })
      .then((resObj: { lat: number; lng: number }) => {
        if (resObj) {
          getServiceability(
            resObj.lat,
            resObj.lng,
            "Search",
            pincode,
            cityName,
            true
          );
        }
      })
      .catch((error: any) => {
        setIsLoading(false);
        console.log("Error from places api", error);
      });
  };

  const renderInnerHTML = ({
    getInputProps,
    suggestions,
    getSuggestionItemProps,
    loading,
  }: InputProps) => {
    return (
      <>
        <div className={classes.locationWrap}>
          <div className={classes.searchWrap}>
            {isFetchingLocation ? (
              <div className={classes.fetchAlert}>Fetching your location..</div>
            ) : (
              <div className={classes.medicineSearchForm}>
                <AphTextField
                  {...getInputProps()}
                  placeholder="Search for street name, pincode…"
                  className={classes.searchInputBox}
                  autoFocus
                />
                <AphButton
                  className={classes.searchBtn}
                  classes={{
                    disabled: classes.searchBtnDisabled,
                  }}
                >
                  {/* <img src={require('images/ic_testsearch.svg')} alt="Search" /> */}
                </AphButton>
              </div>
            )}

            <div
              className={classes.findLocation}
              onClick={locateCurrentLocation}
            >
              {/* <img src={require('images/ic_testmap.png')} alt="Map" /> */}
              Use Current Location
            </div>

            {isUserDeniedLocationAccess && (
              <div className={classes.locationAlert}>
                {/* <img src={require('images/ic_polygon.png')} alt="Other" /> */}
                <div className={classes.locationErrMsg}>
                  {/* <img src={require('images/ic_testlocationwhite.png')} alt="Other" /> */}
                  You have blocked Apollo247 to use location. Please enable them
                  from location settings in your browser
                </div>
              </div>
            )}
          </div>

          <div className={classes.address}>
            {/* for loading */}
            {(loading || isLoading || isFetchingLocation) && (
              <div className={classes.loading}>
                <CircularProgress />
              </div>
            )}
            {/* for savedaddreslist */}
            {!isSearched ? (
              <div className={classes.addressList}>
                {currentPatient?.id && (
                  <>
                    <div className={classes.addressTitle}>Saved Address</div>
                    <div className={classes.addressListWrap}>
                      {deliveryAddresses?.length > 0 &&
                        deliveryAddresses.map(
                          (address) =>
                            address.latitude &&
                            address.longitude && (
                              <div
                                className={classes.addressTile}
                                onClick={() => {
                                  dataLayerTracking({
                                    event: "location_selected",
                                    patient_location: `${address?.city}_${address?.state}`,
                                    state: address?.state,
                                    section: "Lab Tests",
                                  });
                                  setDeliveryAddressId(address.id);
                                  addCartInfo({
                                    cartAddressId: address?.id,
                                  });
                                  getServiceability(
                                    address.latitude,
                                    address.longitude,
                                    "Saved Address",
                                    address.zipcode,
                                    address.city,
                                    true
                                  );
                                }}
                                key={address.id}
                              >
                                <div className={classes.tileTitle}>
                                  <div className={classes.tileTitleContent}>
                                    {/* <img src={require('images/ic_testhome.svg')} alt="Home" /> */}
                                    {address.addressType}
                                  </div>
                                  {address.defaultAddress && (
                                    <div className={classes.tileTitleContent}>
                                      <span>Default</span>
                                    </div>
                                  )}
                                </div>
                                <div className={classes.tileDetail}>
                                  <p>{`${address.addressLine1}, ${address.addressLine2}`}</p>
                                </div>
                              </div>
                            )
                        )}
                    </div>
                  </>
                )}
              </div>
            ) : (
              <div className={classes.searchList}>
                {suggestions.length > 0 ? (
                  suggestions.map((searchResult: SuggestionProps) => {
                    return (
                      <ul>
                        <li
                          key={searchResult.placeId}
                          {...getSuggestionItemProps(searchResult)}
                        >
                          <div className={classes.searchListIcon}>
                            {/* <img src={require('images/ic_testlocation.svg')} alt="Home" /> */}
                          </div>
                          <div className={classes.searchListIcon}>
                            <h4 style={{ cursor: "pointer" }}>
                              {searchResult.formattedSuggestion.mainText}
                            </h4>
                            <p style={{ cursor: "pointer" }}>
                              {searchResult.formattedSuggestion.secondaryText}
                            </p>
                          </div>
                        </li>
                      </ul>
                    );
                  })
                ) : (
                  <p>No Search result found</p>
                )}
              </div>
            )}
          </div>
        </div>
      </>
    );
  };

  useEffect(() => {
    if (updateToDefaultLocation) {
      handleSelect("Hyderabad, Telangana, India");
      setUpdateToDefaultLocation(false);
      setShouldDefault(false);
    }
  }, [updateToDefaultLocation]);

  useEffect(() => {
    if (currentPatient?.id && !deliveryAddresses?.length) {
      client
        .query<GetPatientAddressList, GetPatientAddressListVariables>({
          query: GET_PATIENT_ADDRESSES_LIST,
          variables: {
            patientId: currentPatient.id,
          },
          fetchPolicy: "no-cache",
        })
        .then(({ data }) => {
          if (data?.getPatientAddressList?.addressList) {
            const addresses = data?.getPatientAddressList?.addressList?.reverse();
            if (addresses && addresses.length > 0) {
              setDeliveryAddresses(addresses);
            } else {
              setDeliveryAddresses([]);
            }
          }
        })
        .catch((e) => {
          setDeliveryAddresses([]);
          console.log("Error occured while fetching address", e);
        });
    }
  }, [currentPatient, deliveryAddresses]);

  return (
    <>
      <PlacesAutocomplete
        value={searchInput}
        onChange={handleChange}
        onSelect={handleSelect}
        searchOptions={{
          componentRestrictions: { country: ["in"] },
        }}
      >
        {renderInnerHTML}
      </PlacesAutocomplete>
    </>
  );
};
